import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowBack, Button, Typogaphy } from 'vnetwork-ui'
import FeatureIcons from '@/icons/FeatureIcons'
import FeatureCheckIcons from '@/icons/FeatureCheckIcons'
import FeatureComingSoonIcons from '@/icons/FeatureComingSoonIcons'

interface ComingSoonProps {
    featureName?: string
    description?: string
    expectedDate?: string
    showBackButton?: boolean
    onBack?: () => void
    autoPlay?: boolean
    slideInterval?: number
}

interface SlideData {
    icon: React.ReactNode
    title: string
    description: string
    animation: string
}

const ComingSoon: React.FC<ComingSoonProps> = ({
    featureName = 'somethingexciting',
    description = 'getreadyforapowerfuladditionto',
    expectedDate,
    showBackButton = true,
    onBack,
    autoPlay = true,
    slideInterval = 4000,
}) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [currentSlide, setCurrentSlide] = useState(0)

    const handleBackClick = () => {
        if (onBack) {
            onBack()
        } else {
            navigate(-1)
        }
    }

    const slides: SlideData[] = [
        {
            icon: <FeatureIcons />,
            title: `${t('comingSoon.title', { featureName: t(featureName) })}`,
            description: `${t('comingSoon.description', { description: t(description) })}`,
            animation: 'bounce',
        },
        {
            icon: <FeatureCheckIcons />,
            title: 'Amazing Features',
            description: t('powerfulltools'),
            animation: 'float',
        },
        {
            icon: <FeatureComingSoonIcons />,
            title: 'Coming Soon!',
            description: t('comingSoon.staytuned.description', {
                expectedDate: expectedDate ? ` on ${expectedDate}` : '',
            }),
            animation: 'pulse',
        },
    ]

    // Auto-play functionality
    useEffect(() => {
        if (!autoPlay) return

        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length)
        }, slideInterval)

        return () => clearInterval(interval)
    }, [autoPlay, slideInterval, slides.length])

    const goToSlide = (index: number) => {
        setCurrentSlide(index)
    }

    // Animation classes
    const getAnimationClass = (animation: string, isActive: boolean) => {
        if (!isActive) return ''
        switch (animation) {
            case 'bounce':
                return 'animate-bounce'
            case 'float':
                return 'animate-bounce'
            case 'pulse':
                return 'animate-pulse'
            default:
                return ''
        }
    }

    return (
        <div className="flex flex-col items-center justify-center min-h-[100vh-58px] p-8 text-center relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-green-50">
            <div className="absolute left-[5%] bottom-[10%] opacity-10 -rotate-12">
                <svg viewBox="0 0 24 24" className="w-20 h-20 fill-primary">
                    <path d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z" />
                </svg>
            </div>

            <div className="absolute right-[5%] bottom-[10%] opacity-10 rotate-12">
                <svg viewBox="0 0 24 24" className="w-20 h-20 fill-primary">
                    <path d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z" />
                </svg>
            </div>

            {/* Slider Container */}
            <div className="relative w-full max-w-2xl h-[500px]">
                {slides?.map((slide, index) => (
                    <div
                        key={index}
                        className={`absolute inset-0 flex flex-col items-center justify-center transition-all duration-700 ease-in-out ${
                            index === currentSlide
                                ? 'opacity-100 translate-x-0'
                                : 'opacity-0 translate-x-full'
                        }`}
                        style={{
                            transform: `translateX(${(index - currentSlide) * 100}%)`,
                        }}
                    >
                        {/* Animated Icon */}
                        <div
                            className={`w-36 h-36 mb-8 relative ${getAnimationClass(slide.animation, index === currentSlide)}`}
                        >
                            {slide.icon}
                        </div>

                        {/* Title */}
                        <Typogaphy
                            className={`font-bold mb-4 bg-gradient-to-r from-green-500 to-blue-500 bg-clip-text text-transparent text-3xl md:text-4xl ${
                                index === currentSlide
                                    ? 'animate-fadeInLeft'
                                    : ''
                            }`}
                        >
                            {slide.title}
                        </Typogaphy>

                        {/* Description */}
                        <Typogaphy
                            className={`text-gray-600 max-w-lg leading-relaxed text-base md:text-lg text-center ${
                                index === currentSlide
                                    ? 'animate-fadeInRight'
                                    : ''
                            }`}
                        >
                            {slide.description}
                        </Typogaphy>
                    </div>
                ))}

                {/* Slide Indicators */}
                <div className="absolute bottom-5 left-1/2 transform -translate-x-1/2 flex justify-center gap-2">
                    {slides.map((_, index) => (
                        <div
                            key={index}
                            className={`w-3 h-3 rounded-full cursor-pointer transition-all duration-300 hover:scale-125 ${
                                index === currentSlide
                                    ? 'bg-primary'
                                    : 'bg-gray-300 hover:bg-primary-light'
                            }`}
                            onClick={() => goToSlide(index)}
                        />
                    ))}
                </div>
            </div>

            {/* Back Button */}
            {showBackButton && (
                <div className="mt-8">
                    <Button
                        startIcon={<ArrowBack />}
                        variant="contained"
                        onClick={handleBackClick}
                        size="small"
                        className="rounded-md px-6 py-3 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                    >
                        {t('comingSoon.backtohome')}
                    </Button>
                </div>
            )}
        </div>
    )
}

export default ComingSoon
